import CheckItem from '@allesauto/core/chekItem';
import CircularLoader, {
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import DealerLocationInfo from '@allesauto/core/dealerLocationInfo';
import TextLink from '@allesauto/core/textLink';
import type { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { Add16 } from '@carbon/icons-react';
import clsx from 'clsx';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

export interface DealersListProps {
  dealers?: SchemaTypes.Dealer[];
  maxVisible?: number;
  showBy?: number;
  loading?: boolean;
  selectedDealerId?: string | null;
  onSelect?: (dealer: SchemaTypes.Dealer) => void;
  showPrice: boolean;
  showMoreTranslationKey: string;
  preventLabelClick?: boolean;
  isDealerListType?: boolean;
}

export default function DealersList({
  dealers,
  maxVisible: maxVisibleProp = 12,
  showBy = 3,
  loading = false,
  selectedDealerId,
  onSelect = () => null,
  showPrice,
  showMoreTranslationKey,
  preventLabelClick = true,
  isDealerListType = true,
}: DealersListProps) {
  const intl = useIntl();
  const [visibleCount, setVisibleCount] = useState<number>(showBy);

  const dealersLength = dealers?.length ?? 0;
  const maxVisible =
    dealersLength < maxVisibleProp ? dealersLength : maxVisibleProp;
  const showPaginator = maxVisible > showBy;

  const selectedDealerIndex = useMemo(
    () =>
      selectedDealerId && Array.isArray(dealers)
        ? dealers.findIndex((dealer) => dealer.id === selectedDealerId)
        : showBy,
    [dealers, showBy, selectedDealerId]
  );

  const numberOfVisibleDealers = useMemo(
    () =>
      selectedDealerIndex !== -1
        ? Math.ceil((selectedDealerIndex + 1) / showBy) * showBy
        : showBy,
    [selectedDealerIndex, showBy]
  );

  const visibleDealers = useMemo(
    () =>
      Array.isArray(dealers)
        ? showPaginator
          ? dealers.slice(0, visibleCount)
          : dealers
        : [],
    [dealers, showPaginator, visibleCount]
  );

  useEffect(() => {
    if (dealers?.length) {
      setVisibleCount(numberOfVisibleDealers);
    }
  }, [dealers, numberOfVisibleDealers]);

  const deliveryFee = (dealer: SchemaTypes.Dealer) =>
    dealer.deliveryFee && showPrice ? { price: dealer.deliveryFee } : undefined;

  const className = {
    wrapper: 'flex-1',
    wrapperSpace: 'mt-30px xl:mt-40px',
    loader: 'flex h-full items-center justify-center',
    showMoreLink: 'mt-30px xl:mt-40px',
  };

  const TagManaderHandler = (dealerName: Maybe<string>) => {
    pushAnalyticsEvent({
      event: 'dealer selection',
      dealer_name: dealerName,
    });
  };

  return (
    <div
      className={clsx(
        className.wrapper,
        (loading || visibleDealers.length) && className.wrapperSpace
      )}
    >
      {loading && (
        <div className={className.loader}>
          <CircularLoader type={CircularLoaderType.secondary} />
        </div>
      )}

      {visibleDealers.map((dealer, i) => (
        <CheckItem
          key={dealer.id}
          type={isDealerListType ? 'radio' : undefined}
          selected={selectedDealerId === dealer.id}
          onClick={() => {
            onSelect(dealer);
            TagManaderHandler(dealer.name);
          }}
          className={clsx(i !== 0 && 'mt-15px md:mt-20px')}
          title={dealer.name ?? ''}
          priceProps={deliveryFee(dealer)}
          preventLabelClick={preventLabelClick}
        >
          <DealerLocationInfo dealer={dealer} />
        </CheckItem>
      ))}

      {showPaginator && visibleCount < maxVisible && (
        <TextLink
          text={translate(intl, showMoreTranslationKey)}
          icon={<Add16 />}
          customClassName={className.showMoreLink}
          onClick={() => setVisibleCount((v) => v + showBy)}
        />
      )}
    </div>
  );
}
